import React from "react";
import { useNavigate } from "react-router-dom";
import MyWsgCardBadge from "../Components/MyWsgCardBadge";

const FEATURED_ARTICLE_PREVIEW_MAX_LENGTH = 329;

export interface IArticleCard {
  id: string;
  legacyId: string;
  title: string;
  articleContent?: unknown;
  content?: unknown;
  summary?: unknown;
  publishedDate: string;
  authorName?: string;
  authorTitle?: string;
  authorLastName?: string;
  firmName?: string;
  firmLogos?: string[];
  category?: string;
  tags?: string[];
  imageUrl?: string;
  readTime?: number;
  viewCount?: number;
}

interface IData {
  data: IArticleCard[];
}

const normalizeText = (value: string) =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const extractTextFromArticleContent = (value: unknown): string => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return normalizeText(value);
  }

  if (Array.isArray(value)) {
    return normalizeText(value.map(extractTextFromArticleContent).join(" "));
  }

  if (typeof value === "object") {
    const content = value as Record<string, unknown>;

    if (typeof content.text === "string") {
      return normalizeText(content.text);
    }

    if (content.children) {
      return extractTextFromArticleContent(content.children);
    }

    if (content.content) {
      return extractTextFromArticleContent(content.content);
    }
  }

  return "";
};

const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) {
    return text;
  }

  const truncated = text.slice(0, maxLength).trimEnd();
  const lastSpace = truncated.lastIndexOf(" ");

  return `${truncated.slice(0, lastSpace > 0 ? lastSpace : maxLength)}...`;
};

const Articles: React.FC<IData> = ({ data = [] }) => {
  const navigate = useNavigate();

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col gap-4 text-gray-font justify-center items-center h-full">
        <div
          className="text-center py-4 font-sans text-[#474F56] text-base font-semibold leading-normal overflow-hidden text-ellipsis"
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 3,
            alignSelf: "stretch",
          }}
        >
          You haven't uploaded any articles yet.
          <br />
          Grow your influence adding one today.
        </div>
      </div>
    );
  }

  const formatDate = (date: string) => {
    try {
      return new Date(date).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    } catch (e) {
      return "Invalid date";
    }
  };

  const getAuthorName = (article: IArticleCard) => {
    if (article.authorName) {
      return `${article.authorTitle || ""} ${article.authorName} ${article.authorLastName || ""}`.trim();
    }
    return "Unknown Author";
  };

  const getArticlePreview = (article: IArticleCard) => {
    const previewText =
      [article.articleContent, article.content, article.summary]
        .map(extractTextFromArticleContent)
        .find(Boolean) || "";

    return truncateText(previewText, FEATURED_ARTICLE_PREVIEW_MAX_LENGTH);
  };

  const navigateToArticle = (article: IArticleCard) => {
    navigate(`/publications.asp?action=article&artid=${article.legacyId}`);
  };

  const articleItem = (article: IArticleCard) => (
    <button
      key={article.id || article.legacyId}
      className="flex flex-col text-left"
      onClick={() => navigateToArticle(article)}
    >
      <div className="text-base text-Default hover:text-primary truncate">
        {article.title}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-font underline">
          By {getAuthorName(article)}
        </span>
        <div className="w-px h-4 bg-primary-light"></div>
        <span className="text-sm text-gray-font">
          {article.publishedDate ? formatDate(article.publishedDate) : ""}
        </span>
      </div>
    </button>
  );

  const featuredArticle = data[0];
  const otherArticles = data.slice(1, 3);
  const featuredArticlePreview = featuredArticle
    ? getArticlePreview(featuredArticle)
    : "";

  return (
    <div className="flex flex-col gap-8 text-gray-font w-full max-w-full overflow-hidden">
      <div className="flex flex-col gap-6">
        {featuredArticle && (
          <div className="flex flex-col">
            <MyWsgCardBadge title="Featured Article" />

            <button
              className="flex flex-col text-left"
              onClick={() => navigateToArticle(featuredArticle)}
            >
              <div className="text-xl leading-8 text-gray-font font-semibold hover:text-primary mb-2">
                {featuredArticle.title}
              </div>
              {featuredArticlePreview && (
                <div
                  className="self-stretch text-base leading-6 font-normal overflow-hidden text-gray-font"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    textOverflow: "ellipsis",
                    lineHeight: "24px",
                  }}
                >
                  {featuredArticlePreview}
                </div>
              )}
              <div className="text-sm text-gray-font flex items-center gap-2">
                <span className="underline">
                  By {getAuthorName(featuredArticle)}
                </span>
                <div className="w-px h-4 bg-primary-light"></div>
                <span>
                  {featuredArticle.publishedDate
                    ? formatDate(featuredArticle.publishedDate)
                    : ""}
                </span>
              </div>
            </button>
          </div>
        )}
        {otherArticles.length > 0 && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center w-full">
              <span className="text-right text-lg md:text-xl mr-2 font-semibold">
                Explore more articles
              </span>
              <div className="flex-1 border-t border-primary-light"></div>
            </div>

            <div>
              <div className="flex flex-col gap-4 align-start self-stretch">
                {otherArticles.map((article) => articleItem(article))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Articles;
